/**
 * Campos de data e hora da feature de torneio. O usuário vê e digita
 * DD/MM/AAAA HH:MM (24h); o resto do código continua trabalhando com o
 * formato local AAAA-MM-DDTHH:MM, que é o que a API recebe.
 *
 * Os inputs nativos (`datetime-local`, `time`) mostram o formato do sistema
 * do usuário — MM/DD/AAAA e AM/PM num navegador em inglês — e não dá para
 * forçar outro. Por isso os campos são de texto com máscara.
 */

export const DATETIME_MASK = '99/99/9999 99:99';
export const TIME_MASK = '99:99';

/** Valor de campo preenchido, mas que não forma uma data válida. */
export const INVALID_DATETIME = 'invalid';

const pad = (n: number) => String(n).padStart(2, '0');

const daysInMonth = (year: number, month: number) =>
  new Date(year, month, 0).getDate();

const isValidClock = (hour: number, minute: number) =>
  hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;

/** "2026-11-07T09:30" → "07/11/2026 09:30". Vazio continua vazio. */
export const localToMasked = (local?: string | null): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local ?? '');
  if (!match) return '';
  const [, year, month, day, hour, minute] = match;
  return `${day}/${month}/${year} ${hour}:${minute}`;
};

/**
 * "07/11/2026 09:30" → "2026-11-07T09:30". Sem nada digitado: "".
 * Incompleto ou impossível (31/02, 25:00): INVALID_DATETIME.
 */
export const maskedToLocal = (masked: string): string => {
  const digits = masked.replace(/\D/g, '');
  if (digits.length === 0) return '';

  const match = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/.exec(
    masked.trim(),
  );
  if (!match) return INVALID_DATETIME;

  const [day, month, year, hour, minute] = match.slice(1).map(Number);
  const validDate =
    year >= 2000 &&
    year <= 2100 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth(year, month);

  if (!validDate || !isValidClock(hour, minute)) return INVALID_DATETIME;

  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
};

export const isValidLocalDateTime = (value?: string | null): boolean =>
  !!value && value !== INVALID_DATETIME;

/** "09:30" completo e válido em 24h; senão null. */
export const parseTime = (masked: string): string | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(masked.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return isValidClock(hour, minute) ? `${pad(hour)}:${pad(minute)}` : null;
};
